import { useParams, Link, Navigate } from 'react-router-dom'
import Navbar from '../../components/layout/Navbar'
import Footer from '../../components/layout/Footer'
import './ModulePage.css'

// Module data with detailed information
const modulesData = {
  studio: {
    id: 'studio',
    name: 'Studio',
    tagline: 'Build apps with AI. Ship in minutes.',
    headline: 'The AI-powered app builder that turns ideas into production code.',
    description: 'Studio is your creative partner in software. Describe what you want in plain English, and watch as real, production-ready code materializes before your eyes. No templates, no limitations — just your vision, realized.',
    color: 'studio',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
        <circle cx="11" cy="11" r="2"/>
      </svg>
    ),
    features: [
      {
        title: 'Natural Language Building',
        description: 'Describe your app in plain English. Studio understands context, requirements, and best practices to generate exactly what you need.',
        icon: '💬',
      },
      {
        title: 'Real Code, Full Ownership',
        description: 'Every line of code is yours. React components, clean architecture, no vendor lock-in. Export anytime.',
        icon: '📦',
      },
      {
        title: 'Live Preview & Iteration',
        description: 'See changes in real-time as you refine your vision. Make adjustments through conversation, not configuration.',
        icon: '👁️',
      },
      {
        title: 'One-Click Deploy',
        description: 'Push to production instantly. Custom domains, SSL certificates, and global CDN included with every deployment.',
        icon: '🚀',
      },
      {
        title: 'Component Library',
        description: 'Access hundreds of pre-built components optimized for conversion, accessibility, and performance.',
        icon: '🧩',
      },
      {
        title: 'Integrations Built-In',
        description: 'Connect Chekkouts for payments, Relay for chat — everything works together seamlessly.',
        icon: '🔗',
      },
    ],
    useCases: [
      'Landing pages & marketing sites',
      'SaaS dashboards & admin panels',
      'E-commerce storefronts',
      'Portfolio & personal sites',
      'Internal tools & forms',
      'Documentation sites',
    ],
    stats: [
      { value: '10x', label: 'Faster than traditional development' },
      { value: '2M+', label: 'Sites deployed' },
      { value: '99.9%', label: 'Uptime guaranteed' },
    ],
    integrations: ['Chekkouts', 'Relay', 'Namelyft', 'Keeply'],
    pricing: {
      free: { price: '$0', features: ['3 projects', '500 visitors/mo', 'Hubthrill subdomain'] },
      pro: { price: '$29', features: ['Unlimited projects', '100K visitors/mo', 'Custom domains', 'Analytics'] },
      business: { price: '$99', features: ['Everything in Pro', 'Priority support', 'Team collaboration', 'White-label'] },
    },
  },
  chekkouts: {
    id: 'chekkouts',
    name: 'Chekkouts',
    tagline: 'Accept payments. Everywhere.',
    headline: 'The payment platform built for modern businesses.',
    description: 'Chekkouts handles everything from one-time purchases to complex subscription billing. Accept cards, bank transfers, and digital wallets with one integration. See money in your account faster than ever.',
    color: 'payments',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
    features: [
      {
        title: 'Universal Payment Methods',
        description: 'Accept Visa, Mastercard, Amex, bank transfers, Apple Pay, Google Pay, and local payment methods worldwide.',
        icon: '💳',
      },
      {
        title: 'Hosted Checkout',
        description: 'Conversion-optimized checkout pages that handle cart abandonment, validation, and fraud prevention automatically.',
        icon: '🛒',
      },
      {
        title: 'Subscription Billing',
        description: 'Create pricing tables, manage trials, handle prorations, and automate dunning for failed payments.',
        icon: '🔄',
      },
      {
        title: 'Instant Payouts',
        description: 'Get paid the same day. No waiting periods, no holding reserves. Your money when you need it.',
        icon: '⚡',
      },
      {
        title: 'Invoicing & Quotes',
        description: 'Send professional invoices, track payments, and convert quotes to invoices with one click.',
        icon: '📄',
      },
      {
        title: 'Revenue Analytics',
        description: 'Track MRR, churn, LTV, and revenue trends. Understand your business in real-time.',
        icon: '📊',
      },
    ],
    useCases: [
      'One-time product sales',
      'Subscription SaaS products',
      'Marketplaces & platforms',
      'Digital goods & downloads',
      'Professional services',
      'Donations & fundraising',
    ],
    stats: [
      { value: '$2B+', label: 'Processed annually' },
      { value: '2.4%', label: 'Industry-low fees' },
      { value: '<2 sec', label: 'Average checkout time' },
    ],
    integrations: ['Keeply', 'Relay', 'Studio', 'People'],
    pricing: {
      free: { price: '2.9% + 30¢', features: ['All payment methods', 'Hosted checkout', 'Basic invoicing'] },
      pro: { price: '2.4% + 25¢', features: ['Everything in Free', 'Advanced analytics', 'Subscription billing'] },
      business: { price: 'Custom', features: ['Volume discounts', 'Dedicated support', 'Custom integrations'] },
    },
  },
  keeply: {
    id: 'keeply',
    name: 'Keeply',
    tagline: 'Know your numbers. Always.',
    headline: 'Accounting that actually makes sense.',
    description: 'Keeply is accounting without the accountant. Track expenses, reconcile transactions, generate reports, and stay tax-ready — all with the simplicity you deserve. When connected to Chekkouts, your revenue records itself.',
    color: 'accounting',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
      </svg>
    ),
    features: [
      {
        title: 'Automatic Categorization',
        description: 'Transactions are categorized intelligently. Receipt scanning extracts vendor, amount, and category automatically.',
        icon: '🏷️',
      },
      {
        title: 'Bank Reconciliation',
        description: 'Connect your bank accounts and credit cards. Transactions import daily and match to your records automatically.',
        icon: '🏦',
      },
      {
        title: 'Financial Statements',
        description: 'Generate P&L, balance sheet, and cash flow statements on demand. Always accurate, always current.',
        icon: '📈',
      },
      {
        title: 'Tax Preparation',
        description: 'Export tax-ready reports. Calculate estimated taxes. Never scramble at year-end again.',
        icon: '📋',
      },
      {
        title: 'Multi-Currency',
        description: 'Handle international transactions with automatic conversion. Track forex gains and losses accurately.',
        icon: '🌍',
      },
      {
        title: 'Chekkouts Sync',
        description: 'Revenue from Chekkouts flows directly into your books. No manual entry, no discrepancies.',
        icon: '🔄',
      },
    ],
    useCases: [
      'Freelancer bookkeeping',
      'Small business accounting',
      'E-commerce tracking',
      'Project-based billing',
      'Multi-currency businesses',
      'Tax preparation',
    ],
    stats: [
      { value: '10hrs', label: 'Saved per month on bookkeeping' },
      { value: '99.7%', label: 'Reconciliation accuracy' },
      { value: '50K+', label: 'Businesses trust Keeply' },
    ],
    integrations: ['Chekkouts', 'People', 'Major banks', 'Tax software'],
    pricing: {
      free: { price: '$0', features: ['Basic bookkeeping', 'Bank connections', 'Mobile app'] },
      pro: { price: '$19', features: ['Everything in Free', 'Multi-currency', 'Financial statements', 'Receipt scanning'] },
      business: { price: '$49', features: ['Everything in Pro', 'Multiple businesses', 'Accountant access', 'API access'] },
    },
  },
  relay: {
    id: 'relay',
    name: 'Relay',
    tagline: 'Talk to customers. Build relationships.',
    headline: 'Customer support that feels personal at any scale.',
    description: 'Relay brings live chat, help desk, and customer communication into one unified inbox. Embed chat on your site, manage conversations across channels, and use automation to respond faster — while keeping the human touch.',
    color: 'relay',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    features: [
      {
        title: 'Live Chat Widget',
        description: 'Add real-time chat to any website with one line of code. Customize colors, position, and behavior.',
        icon: '💬',
      },
      {
        title: 'Unified Inbox',
        description: 'Email, chat, social messages — all in one timeline. Never lose context when customers switch channels.',
        icon: '📥',
      },
      {
        title: 'Smart Automation',
        description: 'Auto-routing, canned responses, and chatbots handle the routine so you can focus on complex issues.',
        icon: '🤖',
      },
      {
        title: 'Customer Context',
        description: 'See purchase history from Chekkouts, site activity from Studio — full context at a glance.',
        icon: '👤',
      },
      {
        title: 'Team Collaboration',
        description: 'Internal notes, mentions, and assignments. Collaborate on tickets without the customer seeing.',
        icon: '👥',
      },
      {
        title: 'Knowledge Base',
        description: 'Create self-service docs. Suggest articles automatically. Deflect tickets before they\'re created.',
        icon: '📚',
      },
    ],
    useCases: [
      'E-commerce support',
      'SaaS customer success',
      'Lead qualification',
      'Technical support',
      'Sales assistance',
      'Onboarding help',
    ],
    stats: [
      { value: '45%', label: 'Faster response times' },
      { value: '30%', label: 'Ticket deflection with KB' },
      { value: '4.8★', label: 'Average CSAT score' },
    ],
    integrations: ['Studio', 'Chekkouts', 'Slack', 'Email providers'],
    pricing: {
      free: { price: '$0', features: ['1 chat widget', '100 conversations/mo', 'Email notifications'] },
      pro: { price: '$39', features: ['Unlimited widgets', 'Unlimited conversations', 'Automation', 'Knowledge base'] },
      business: { price: '$99', features: ['Everything in Pro', 'API access', 'Custom integrations', 'SLA support'] },
    },
  },
  people: {
    id: 'people',
    name: 'People',
    tagline: 'Your team, simplified.',
    headline: 'HR and payroll that runs itself.',
    description: 'People handles the human side of business — onboarding, payroll, time-off, and compliance. Set up once and let it handle the rest. Costs sync directly to Keeply, closing the loop between people and finance.',
    color: 'people',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    features: [
      {
        title: 'Employee Directory',
        description: 'Centralized records for your entire team. Documents, contacts, org charts — all searchable.',
        icon: '📇',
      },
      {
        title: 'Payroll Processing',
        description: 'Run payroll in minutes. Tax calculations, deductions, and direct deposits handled automatically.',
        icon: '💰',
      },
      {
        title: 'Time-Off Management',
        description: 'PTO requests, approvals, and balance tracking. Policies enforced automatically.',
        icon: '🏖️',
      },
      {
        title: 'Onboarding Workflows',
        description: 'New hire checklists, document collection, and equipment requests in one automated flow.',
        icon: '🎯',
      },
      {
        title: 'Performance Reviews',
        description: 'Set goals, collect feedback, and run review cycles. Templates and schedules included.',
        icon: '📝',
      },
      {
        title: 'Keeply Integration',
        description: 'Payroll costs flow directly into your accounting. No manual journal entries needed.',
        icon: '🔗',
      },
    ],
    useCases: [
      'Small team payroll',
      'Growing startup HR',
      'Remote team management',
      'Contractor payments',
      'Compliance tracking',
      'Benefits administration',
    ],
    stats: [
      { value: '5min', label: 'Average payroll run time' },
      { value: '100%', label: 'Tax filing accuracy' },
      { value: '24hr', label: 'Onboarding setup' },
    ],
    integrations: ['Keeply', 'Chekkouts', 'Benefits providers', 'Background check'],
    pricing: {
      free: { price: '$0', features: ['Up to 5 employees', 'Basic directory', 'Time-off tracking'] },
      pro: { price: '$8/employee', features: ['Unlimited employees', 'Full payroll', 'Onboarding', 'Performance'] },
      business: { price: '$15/employee', features: ['Everything in Pro', 'Benefits admin', 'API access', 'Compliance pack'] },
    },
  },
  namelyft: {
    id: 'namelyft',
    name: 'Namelyft',
    tagline: 'Infrastructure without the headache.',
    headline: 'Cloud hosting that just works.',
    description: 'Namelyft takes the complexity out of infrastructure. Domain registration, managed hosting, automatic deployments, and global CDN — all in one place. No DevOps degree required.',
    color: 'infrastructure',
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="2" width="20" height="8" rx="2"/>
        <rect x="2" y="14" width="20" height="8" rx="2"/>
        <circle cx="6" cy="6" r="1" fill="currentColor"/>
        <circle cx="6" cy="18" r="1" fill="currentColor"/>
      </svg>
    ),
    features: [
      {
        title: 'Managed Hosting',
        description: 'Auto-scaling, load balancing, and health monitoring. Your app stays up, we handle the rest.',
        icon: '☁️',
      },
      {
        title: 'Domain Management',
        description: 'Register domains, configure DNS, and provision SSL certificates — all from one dashboard.',
        icon: '🌐',
      },
      {
        title: 'Git Deployments',
        description: 'Push to deploy. Every commit triggers a build. Preview URLs for every pull request.',
        icon: '🔧',
      },
      {
        title: 'Global CDN',
        description: 'Content served from 200+ locations worldwide. Automatic caching, instant invalidation.',
        icon: '⚡',
      },
      {
        title: 'Database Hosting',
        description: 'Managed PostgreSQL and Redis. Automatic backups, point-in-time recovery, replicas.',
        icon: '💾',
      },
      {
        title: 'Studio Integration',
        description: 'Sites built in Studio deploy to Namelyft automatically. Zero configuration needed.',
        icon: '🔗',
      },
    ],
    useCases: [
      'Production app hosting',
      'Static site deployment',
      'API and backend services',
      'Database management',
      'Domain registration',
      'SSL certificates',
    ],
    stats: [
      { value: '99.99%', label: 'Uptime SLA' },
      { value: '50ms', label: 'Average global latency' },
      { value: '200+', label: 'Edge locations' },
    ],
    integrations: ['Studio', 'GitHub', 'GitLab', 'Bitbucket'],
    pricing: {
      free: { price: '$0', features: ['1 project', 'Hubthrill subdomain', '100GB bandwidth'] },
      pro: { price: '$20', features: ['Unlimited projects', 'Custom domains', '1TB bandwidth', 'SSL included'] },
      business: { price: '$100', features: ['Everything in Pro', 'Dedicated support', 'SLA guarantee', 'Database hosting'] },
    },
  },
}

function ModulePage() {
  const { moduleId } = useParams()
  const module = modulesData[moduleId]

  // Redirect to 404 if module not found
  if (!module) {
    return <Navigate to="/404" replace />
  }

  return (
    <div className="module-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className={`module-hero module-hero-${module.color}`}>
        <div className="container">
          <div className="module-hero-content">
            <div className={`module-hero-icon module-icon-${module.color}`}>
              {module.icon}
            </div>
            <span className="module-hero-label">{module.name}</span>
            <h1 className="module-hero-title">{module.headline}</h1>
            <p className="module-hero-description">{module.description}</p>
            <div className="module-hero-actions">
              <Link to="/signup" className="btn btn-primary btn-lg">
                Start with {module.name}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </Link>
              <Link to="/demo" className="btn btn-secondary btn-lg">
                See it in action
              </Link>
            </div>
          </div>
        </div>
        <div className="module-hero-bg"></div>
      </section>

      {/* Stats Section */}
      <section className="module-stats">
        <div className="container">
          <div className="stats-grid">
            {module.stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <span className={`stat-value stat-value-${module.color}`}>{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="module-features section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Features</span>
            <h2 className="section-title">
              Everything you need to {module.tagline.toLowerCase().replace('.', '')}
            </h2>
          </div>
          <div className="features-grid">
            {module.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <span className="feature-emoji">{feature.icon}</span>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="module-use-cases section">
        <div className="container">
          <div className="use-cases-content">
            <div className="use-cases-header">
              <span className="section-label">Use Cases</span>
              <h2 className="section-title">Built for how you work</h2>
              <p className="section-subtitle">
                {module.name} adapts to your business, whether you're just starting 
                out or scaling to millions of customers.
              </p>
            </div>
            <div className="use-cases-list">
              {module.useCases.map((useCase, index) => (
                <div key={index} className={`use-case-item use-case-${module.color}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>{useCase}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="module-integrations section">
        <div className="container">
          <div className="integrations-content">
            <div className="integrations-text">
              <span className="section-label">Ecosystem</span>
              <h2 className="section-title">
                Better with the Hubthrill ecosystem
              </h2>
              <p className="section-subtitle">
                {module.name} connects seamlessly with other Hubthrill modules. 
                Data flows automatically, so you never have to copy-paste between tools.
              </p>
              <div className="integrations-list">
                {module.integrations.map((integration, index) => (
                  <span key={index} className="integration-badge">{integration}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="module-pricing section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Pricing</span>
            <h2 className="section-title">Simple, transparent pricing</h2>
            <p className="section-subtitle">
              Start free, upgrade when you're ready. No hidden fees, no surprises.
            </p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3 className="pricing-tier">Free</h3>
              <div className="pricing-price">
                <span className="price">{module.pricing.free.price}</span>
                {!module.pricing.free.price.includes('%') && <span className="period">/month</span>}
              </div>
              <ul className="pricing-features">
                {module.pricing.free.features.map((feature, index) => (
                  <li key={index}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="btn btn-secondary btn-block">
                Get started free
              </Link>
            </div>
            <div className={`pricing-card pricing-card-featured pricing-card-${module.color}`}>
              <span className="pricing-badge">Most Popular</span>
              <h3 className="pricing-tier">Pro</h3>
              <div className="pricing-price">
                <span className="price">{module.pricing.pro.price}</span>
                {!module.pricing.pro.price.includes('%') && <span className="period">/month</span>}
              </div>
              <ul className="pricing-features">
                {module.pricing.pro.features.map((feature, index) => (
                  <li key={index}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="btn btn-primary btn-block">
                Start Pro trial
              </Link>
            </div>
            <div className="pricing-card">
              <h3 className="pricing-tier">Business</h3>
              <div className="pricing-price">
                <span className="price">{module.pricing.business.price}</span>
                {!module.pricing.business.price.includes('%') && module.pricing.business.price !== 'Custom' && <span className="period">/month</span>}
              </div>
              <ul className="pricing-features">
                {module.pricing.business.features.map((feature, index) => (
                  <li key={index}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-secondary btn-block">
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`module-cta module-cta-${module.color}`}>
        <div className="container">
          <div className="cta-content">
            <h2>Ready to try {module.name}?</h2>
            <p>Join thousands of businesses already using {module.name} to {module.tagline.toLowerCase().replace('.', '')}.</p>
            <Link to="/signup" className="btn btn-white btn-lg">
              Create free account
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ModulePage
